import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, Ticket, X } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";

const money = (c, v) => `${c || ""} ${Number(v || 0).toLocaleString("en-US", { maximumFractionDigits: 2 })}`;
const badge = (s) => s === "confirmed" || s === "issued"
  ? "bg-emerald-50 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300"
  : "bg-slate-100 text-slate-500 dark:bg-ink-panel dark:text-slate-400";

export default function Bookings() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);

  useEffect(() => {
    (async () => {
      try { setRows((await api.get("/finance/bookings")).data); }
      catch (e) { toast.error(apiError(e)); } finally { setLoading(false); }
    })();
  }, []);

  const openDetail = async (id) => {
    try { setDetail((await api.get(`/finance/bookings/${id}`)).data); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="space-y-6" data-testid="bookings-page">
      <div>
        <p className="text-xs font-medium uppercase tracking-wider text-slate-400">Confirmed Operations</p>
        <h1 className="font-display text-2xl font-bold tracking-tight text-slate-900 dark:text-ink-text">Bookings</h1>
        <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">A Booking is born from a Pro Forma Invoice only after its required payment is verified by a human.</p>
      </div>

      {loading ? (
        <div className="flex h-64 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-sky-600 dark:text-ink-gold" /></div>
      ) : rows.length === 0 ? (
        <div className="rounded-xl border border-dashed border-slate-300 p-12 text-center dark:border-ink-line" data-testid="bookings-empty-state">
          <Ticket className="mx-auto h-8 w-8 text-slate-400" />
          <p className="mt-3 text-sm font-medium text-slate-600 dark:text-slate-300">No bookings yet</p>
          <p className="mt-1 text-xs text-slate-400">Verify a Pro Forma Invoice payment to create the first Booking.</p>
        </div>
      ) : (
        <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm dark:border-ink-line dark:bg-ink-card">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="bookings-table">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500 dark:border-ink-line dark:bg-ink/40">
                  <th className="px-5 py-3 font-medium">Booking #</th>
                  <th className="px-5 py-3 font-medium">Customer</th>
                  <th className="px-5 py-3 font-medium">PI #</th>
                  <th className="px-5 py-3 font-medium">Terms</th>
                  <th className="px-5 py-3 font-medium">Total</th>
                  <th className="px-5 py-3 font-medium">Verified Deposit</th>
                  <th className="px-5 py-3 font-medium">Status</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} onClick={() => openDetail(r.id)} className="cursor-pointer border-b border-slate-100 last:border-0 hover:bg-slate-50 dark:border-ink-line dark:hover:bg-ink-panel/40" data-testid={`booking-row-${r.id}`}>
                    <td className="px-5 py-3 font-mono-tf text-xs font-semibold text-slate-700 dark:text-ink-gold">{r.booking_number}</td>
                    <td className="px-5 py-3 font-medium text-slate-800 dark:text-ink-text">{r.customer_name || "—"}</td>
                    <td className="px-5 py-3 font-mono-tf text-xs text-slate-600 dark:text-slate-300">{r.proforma_number || "—"}</td>
                    <td className="px-5 py-3 text-slate-600 dark:text-slate-300">{r.payment_terms || "—"}</td>
                    <td className="px-5 py-3 font-mono-tf text-slate-700 dark:text-slate-200">{money(r.currency, r.total_amount)}</td>
                    <td className="px-5 py-3 font-mono-tf text-slate-700 dark:text-slate-200">{money(r.currency, r.verified_deposit_amount)}</td>
                    <td className="px-5 py-3"><span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${badge(r.status)}`}>{r.status}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {detail && <BookingDetail b={detail} onClose={() => setDetail(null)} />}
    </div>
  );
}

function BookingDetail({ b, onClose }) {
  const rel = b.related || {};
  const R = ({ label, children }) => (
    <div className="flex justify-between gap-4 border-b border-slate-100 py-2 last:border-0 dark:border-ink-line">
      <span className="text-slate-500 dark:text-slate-400">{label}</span>
      <span className="text-right font-medium text-slate-800 dark:text-ink-text">{children}</span>
    </div>
  );
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="booking-detail-modal">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative w-full max-w-lg rounded-2xl border border-slate-200 bg-white p-6 shadow-2xl dark:border-ink-line dark:bg-ink-card">
        <div className="mb-4 flex items-center justify-between">
          <div>
            <p className="text-xs font-medium uppercase tracking-wider text-slate-400">Booking</p>
            <h2 className="font-display text-xl font-bold text-slate-900 dark:text-ink-text" data-testid="booking-detail-number">{b.booking_number}</h2>
          </div>
          <button onClick={onClose} className="rounded-md p-1 text-slate-400 hover:bg-slate-100 dark:hover:bg-ink-panel"><X className="h-5 w-5" /></button>
        </div>
        <div className="text-sm">
          <R label="Customer">{b.customer_name || "—"}</R>
          <R label="Status">{b.status}</R>
          <R label="Total amount">{money(b.currency, b.total_amount)}</R>
          <R label="Verified payment">{money(b.currency, b.verified_deposit_amount)}</R>
          <R label="Pro Forma Invoice">{rel.proforma_invoice?.document_number || "—"} {rel.proforma_invoice?.payment_terms ? `(${rel.proforma_invoice.payment_terms})` : ""}</R>
          <R label="Quotation">{rel.quotation?.quotation_number || "—"}</R>
          <R label="Deposit receipts">{(rel.deposit_receipts || []).map((r) => r.document_number || "draft").join(", ") || "—"}</R>
          <R label="Invoices">{(rel.invoices || []).map((i) => i.document_number || "draft").join(", ") || "—"}</R>
        </div>
      </div>
    </div>
  );
}
